import { Action, action } from "easy-peasy";

// Holds the "additional data collection" fields entered on the Supplier Payment
// (card) and Customer Payment (billing profile) tabs, so a value typed on one
// tab is still there if the user switches to the other tab and back.
//
// Two buckets only: cardAdditionalData (Supplier Payment's own fields) and
// billingProfileAdditionalData (Customer Payment's own fields). Each carries
// an `identifier` (which card / which customer code the values belong to)
// alongside the `values`. Callers should always go through
// updateCardAdditionalData / updateBillingProfileAdditionalData with the
// *current* identifier — if it doesn't match what's stored, the bucket is
// replaced instead of merged, so switching to a different card or a different
// customer code starts that bucket fresh rather than showing stale values.
//
// A field whose maps_to name is rendered on BOTH pages (for whichever
// card/customer code is currently active) is kept in sync directly between
// the two buckets: whichever side writes a value for that key, the SAME
// value is mirrored into the other bucket's `values` too, so the other page
// sees it once the user switches to it. `cardFields`/`billingFields` are the
// CMS field definitions each page currently has rendered (not values) —
// published by each page so the update actions below can tell which of the
// keys being written also matter on the other side. `key` is the exact
// string used to index `values` for that field: the bare maps_to for an
// order-context field, or `${maps_to}_${paxRef}` for a passenger-context
// one — that suffix is what keeps two passengers' fields (or an
// order-context field vs. a passenger-context one) with the same maps_to
// from ever being treated as the same field.
export interface FieldMeta {
  key: string;
  maps_to: string;
  field_context: string;
  name?: string;
  description?: string;
  paxRef?: string;
}

export interface FieldsBucket {
  identifier: string | null;
  values: { [key: string]: any };
}

// Excludes only genuinely absent values. This used to also treat "" as
// absent, to stop a field's blank starting value (from react-hook-form's
// watch() firing once on registration, with a whole-form snapshot) from
// clobbering a real stored value for some OTHER, untouched field. Now that
// each field's own onChange writes its own value directly and immediately
// (see CustomerPayment.tsx / SupplierPayment.tsx), "" is a legitimate,
// deliberate edit — the user clearing the field — and must be allowed
// through, or the controlled input snaps back to the old value and the
// last character looks impossible to delete.
const hasValue = (value: any) => value !== undefined && value !== null;

const nonEmptyEntries = (patch: { [key: string]: any }) => {
  const result: { [key: string]: any } = {};
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (hasValue(value)) result[key] = value;
  });
  return result;
};

export interface PaymentAdditionalDataModel {
  orderKey: string | null;
  cardAdditionalData: FieldsBucket;
  billingProfileAdditionalData: FieldsBucket;
  // The selected customer profile itself (not just its typed field values) —
  // Customer Payment is fully remounted on a tab switch (Supplier/Customer are
  // separate routes), so without this the selection UI would show nothing
  // selected even though the fields underneath are meant to carry forward.
  selectedCustomerProfile: any;

  // cardFieldKeys: string[];
  // billingFieldKeys: string[];
  cardFields: FieldMeta[];
  billingFields: FieldMeta[];

  setPaymentOrderKey: Action<PaymentAdditionalDataModel, string | null>;
  setSelectedCustomerProfileInStore: Action<PaymentAdditionalDataModel, any>;
  updateCardAdditionalData: Action<
    PaymentAdditionalDataModel,
    { identifier: string | null; patch: { [key: string]: any } }
  >;
  clearCardAdditionalData: Action<PaymentAdditionalDataModel>;
  updateBillingProfileAdditionalData: Action<
    PaymentAdditionalDataModel,
    { identifier: string | null; patch: { [key: string]: any } }
  >;
  clearBillingProfileAdditionalData: Action<PaymentAdditionalDataModel>;
  // setCardFieldKeys: Action<PaymentAdditionalDataModel, string[]>;
  // setBillingFieldKeys: Action<PaymentAdditionalDataModel, string[]>;
  setCardFields: Action<PaymentAdditionalDataModel, FieldMeta[]>;
  setBillingFields: Action<PaymentAdditionalDataModel, FieldMeta[]>;
  // // Replaced — values are now mirrored directly between the two buckets
  // // above instead of being routed through a separate shared bucket.
  // sharedAdditionalData: { [key: string]: any };
  // updateSharedAdditionalData: Action<
  //   PaymentAdditionalDataModel,
  //   { [key: string]: any }
  // >;
  // clearSharedAdditionalData: Action<PaymentAdditionalDataModel>;
  clearPaymentAdditionalData: Action<PaymentAdditionalDataModel>;
}

const PaymentAdditionalDataStore: PaymentAdditionalDataModel = {
  orderKey: null,
  cardAdditionalData: { identifier: null, values: {} },
  billingProfileAdditionalData: { identifier: null, values: {} },
  selectedCustomerProfile: null,
  // sharedAdditionalData: {},
  // cardFieldKeys: [],
  // billingFieldKeys: [],
  cardFields: [],
  billingFields: [],

  // Called once when Payment mounts for a given order. If it's a different
  // order than the one we last tracked, both buckets are wiped so data
  // collected for order A never leaks into order B.
  setPaymentOrderKey: action((state, orderKey) => {
    if (state.orderKey !== orderKey) {
      state.orderKey = orderKey;
      state.cardAdditionalData = { identifier: null, values: {} };
      state.billingProfileAdditionalData = { identifier: null, values: {} };
      state.selectedCustomerProfile = null;
      // state.sharedAdditionalData = {};
      // state.cardFieldKeys = [];
      // state.billingFieldKeys = [];
      state.cardFields = [];
      state.billingFields = [];
    }
  }),

  setSelectedCustomerProfileInStore: action((state, profile) => {
    state.selectedCustomerProfile = profile;
  }),

  // Writes Supplier Payment's own fields, and mirrors any of those fields
  // that Customer Payment also currently renders (per billingFieldKeys) into
  // billingProfileAdditionalData too.
  updateCardAdditionalData: action((state, { identifier, patch }) => {
    const cleanPatch = nonEmptyEntries(patch);
    console.log("[PAD-DEBUG] updateCardAdditionalData called", {
      previousIdentifier: state.cardAdditionalData.identifier,
      identifier,
      rawPatch: patch,
      cleanPatch,
      willReplace: state.cardAdditionalData.identifier !== identifier,
    });
    if (!isEmptyObj(cleanPatch) || state.cardAdditionalData.identifier !== identifier) {
      if (state.cardAdditionalData.identifier !== identifier) {
        state.cardAdditionalData = { identifier, values: { ...cleanPatch } };
      } else {
        state.cardAdditionalData = {
          identifier,
          values: { ...state.cardAdditionalData.values, ...cleanPatch },
        };
      }
    }

    const billingKeys = new Set(state.billingFields.map((f) => f.key));
    const mirrored: { [key: string]: any } = {};
    Object.entries(cleanPatch).forEach(([key, value]) => {
      if (billingKeys.has(key)) mirrored[key] = value;
    });
    if (!isEmptyObj(mirrored)) {
      console.log("[PAD-DEBUG] updateCardAdditionalData mirroring into billingProfileAdditionalData", mirrored);
      state.billingProfileAdditionalData = {
        identifier: state.billingProfileAdditionalData.identifier,
        values: { ...state.billingProfileAdditionalData.values, ...mirrored },
      };
    }
  }),

  clearCardAdditionalData: action((state) => {
    console.log("[PAD-DEBUG] clearCardAdditionalData called", {
      previousValues: state.cardAdditionalData.values,
    });
    state.cardAdditionalData = { identifier: null, values: {} };
  }),

  // Writes Customer Payment's own fields, and mirrors any of those fields
  // that Supplier Payment also currently renders (per cardFieldKeys) into
  // cardAdditionalData too.
  updateBillingProfileAdditionalData: action((state, { identifier, patch }) => {
    const cleanPatch = nonEmptyEntries(patch);
    console.log("[PAD-DEBUG] updateBillingProfileAdditionalData called", {
      previousIdentifier: state.billingProfileAdditionalData.identifier,
      identifier,
      rawPatch: patch,
      cleanPatch,
      willReplace: state.billingProfileAdditionalData.identifier !== identifier,
    });
    if (!isEmptyObj(cleanPatch) || state.billingProfileAdditionalData.identifier !== identifier) {
      if (state.billingProfileAdditionalData.identifier !== identifier) {
        state.billingProfileAdditionalData = { identifier, values: { ...cleanPatch } };
      } else {
        state.billingProfileAdditionalData = {
          identifier,
          values: { ...state.billingProfileAdditionalData.values, ...cleanPatch },
        };
      }
    }

    const cardKeys = new Set(state.cardFields.map((f) => f.key));
    const mirrored: { [key: string]: any } = {};
    Object.entries(cleanPatch).forEach(([key, value]) => {
      if (cardKeys.has(key)) mirrored[key] = value;
    });
    if (!isEmptyObj(mirrored)) {
      console.log("[PAD-DEBUG] updateBillingProfileAdditionalData mirroring into cardAdditionalData", mirrored);
      state.cardAdditionalData = {
        identifier: state.cardAdditionalData.identifier,
        values: { ...state.cardAdditionalData.values, ...mirrored },
      };
    }
  }),

  clearBillingProfileAdditionalData: action((state) => {
    console.log("[PAD-DEBUG] clearBillingProfileAdditionalData called", {
      previousValues: state.billingProfileAdditionalData.values,
      stackHint: new Error().stack?.split("\n").slice(1, 5).join(" | "),
    });
    state.billingProfileAdditionalData = { identifier: null, values: {} };
  }),

  // setCardFieldKeys: action((state, keys) => {
  //   state.cardFieldKeys = keys;
  // }),

  // setBillingFieldKeys: action((state, keys) => {
  //   state.billingFieldKeys = keys;
  // }),

  setCardFields: action((state, fields) => {
    state.cardFields = fields;
  }),

  setBillingFields: action((state, fields) => {
    state.billingFields = fields;
  }),

  // updateSharedAdditionalData: action((state, patch) => {
  //   state.sharedAdditionalData = { ...state.sharedAdditionalData, ...patch };
  // }),

  // clearSharedAdditionalData: action((state) => {
  //   state.sharedAdditionalData = {};
  // }),

  // Called when the user explicitly leaves the Payment flow (Cancel, a
  // breadcrumb link, or a successful issue) — as opposed to just switching
  // between the Supplier/Customer tabs, which should NOT clear anything.
  clearPaymentAdditionalData: action((state) => {
    state.orderKey = null;
    state.cardAdditionalData = { identifier: null, values: {} };
    state.billingProfileAdditionalData = { identifier: null, values: {} };
    state.selectedCustomerProfile = null;
    // state.sharedAdditionalData = {};
    // state.cardFieldKeys = [];
    // state.billingFieldKeys = [];
    state.cardFields = [];
    state.billingFields = [];
  }),
};

function isEmptyObj(obj: { [key: string]: any }) {
  return Object.keys(obj).length === 0;
}

export default PaymentAdditionalDataStore;
